import React, { useState, useEffect, useRef, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRobot, FaTimes, FaPaperPlane, FaInfoCircle, FaExpand, FaCompress } from 'react-icons/fa';
import { BsChatDotsFill } from 'react-icons/bs';
import { Context } from '../../context';
import './Chatbot.css';
import chatbotData from '../../data/chatbotData.json';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const { lang } = useContext(Context);

  const [messages, setMessages] = useState([
    {
      id: 1,
      text: chatbotData.welcome_message?.[lang === 'it' ? 'ita' : 'eng'] || chatbotData.welcome_message?.ita,
      sender: "ai"
    }
  ]);
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  // Forza l'uso dell'API in produzione per aggirare il blocco di localhost/PHP
  const API_URL = "https://chatbot-ai.davidebalice.dev/api.php";

  const defaultSuggestions = {
    it: ["Quali sono i tuoi servizi?", "Come posso contattarti?", "Di cosa ti occupi?", "Parlami del progetto Room Maker 3D"],
    en: ["What are your services?", "How can I contact you?", "What do you do?", "Tell me about Room Maker 3D project"]
  };

  const toggleChat = () => setIsOpen(!isOpen);
  const toggleMaximize = () => setIsMaximized(!isMaximized);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Aggiorna il messaggio iniziale al cambio lingua se è l'unico presente
  useEffect(() => {
    if (messages.length === 1) {
      const msg = chatbotData.welcome_message?.[lang === 'it' ? 'ita' : 'eng'] || chatbotData.welcome_message?.ita;
      setMessages([{ id: 1, text: msg, sender: "ai" }]);
    }
  }, [lang]);

  // Caricamento suggerimenti dal file JSON importato
  useEffect(() => {
    if (isOpen) {
      const langKey = lang === 'it' ? 'ita' : 'eng';
      setSuggestions(chatbotData.suggestions?.[langKey] || defaultSuggestions[lang] || defaultSuggestions.it);
    }
  }, [lang, isOpen]);


  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = text.trim();
    const newUserMsg = { id: Date.now(), text: userMessage, sender: "user" };
    setMessages((prev) => [...prev, newUserMsg]);
    setInputText("");
    setIsTyping(true);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userMessage,
          lang: lang === 'it' ? 'ita' : 'eng',
          engine: 'hugging'
        }),
      });

      if (!response.ok) throw new Error("Errore nella risposta del server");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let aiMessage = "";
      let buffer = "";

      const aiMessageId = Date.now() + 1;
      setIsTyping(false);
      setMessages((prev) => [...prev, { id: aiMessageId, text: "", sender: "ai" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;

          try {
            const jsonStr = trimmedLine.slice(6);
            if (!jsonStr) continue;

            const data = JSON.parse(jsonStr);
            if (data.chunk) {
              aiMessage += data.chunk;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiMessageId ? { ...msg, text: aiMessage } : msg
                )
              );
            } else if (data.error) {
              console.error("Errore ricevuto dall'API:", data.error);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiMessageId ? { ...msg, text: "Errore: " + data.error } : msg
                )
              );
            }
          } catch (err) {
            console.warn("Errore nel parsing del chunk (parziale?):", err, "Line:", trimmedLine);
          }
        }
      }
    } catch (error) {
      console.error("Errore fatale nella chat:", error);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: `Spiacente, errore di connessione: ${error.message}. Verifica la console per i dettagli.`,
          sender: "ai"
        }
      ]);
    }
  };

  const handleSend = (e) => {
    e.preventDefault();
    sendMessage(inputText);
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  return (
    <div className="chatbot-container">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={`chatbot-window ${isMaximized ? 'maximized' : ''}`}
            initial={{ opacity: 0, y: 40, scale: 0.8, originX: 1, originY: 1 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
          >
            <div className="chatbot-header">
              <div className="chatbot-header-info">
                <div className="chatbot-avatar-container">
                  <FaRobot className="chatbot-avatar-icon" />
                  <span className="chatbot-online-dot"></span>
                </div>
                <div className="chatbot-title-wrapper">
                  <h3>Chatbot AI</h3>
                  <p>Sempre online</p>
                </div>
              </div>
              <div className="chatbot-header-actions">
                <button
                  className="chatbot-info-btn"
                  onClick={() => setIsInfoOpen(true)}
                  aria-label="Informazioni"
                  title="Informazioni sull'AI"
                >
                  <FaInfoCircle />
                </button>
                <button
                  className="chatbot-maximize-btn"
                  onClick={toggleMaximize}
                  aria-label={isMaximized ? "Rimpicciolisci" : "Ingrandisci"}
                  title={isMaximized ? "Rimpicciolisci" : "Ingrandisci"}
                >
                  {isMaximized ? <FaCompress /> : <FaExpand />}
                </button>
                <button className="chatbot-close-btn" onClick={toggleChat} aria-label="Chiudi chat">
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="chatbot-messages">
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  className={`chatbot-message-wrapper ${msg.sender}`}
                  initial={{ opacity: 0, x: msg.sender === 'user' ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {msg.sender === 'ai' && (
                    <div className="chatbot-message-avatar">
                      <FaRobot />
                    </div>
                  )}
                  <div className={`chatbot-message ${msg.sender}`}>
                    {msg.text}
                  </div>
                </motion.div>
              ))}

              {isTyping && (
                <motion.div
                  className="chatbot-message-wrapper ai"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="chatbot-message-avatar">
                    <FaRobot />
                  </div>
                  <div className="chatbot-message ai typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chatbot-suggestions">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="suggestion-chip"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>

            <form className="chatbot-input-area" onSubmit={handleSend}>
              <input
                type="text"
                placeholder="Scrivi un messaggio..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
              />
              <motion.button
                type="submit"
                className="chatbot-send-btn"
                disabled={!inputText.trim()}
                whileHover={{ scale: inputText.trim() ? 1.1 : 1 }}
                whileTap={{ scale: inputText.trim() ? 0.9 : 1 }}
              >
                <FaPaperPlane />
              </motion.button>
            </form>

            <AnimatePresence>
              {isInfoOpen && (
                <motion.div
                  className="chatbot-info-modal-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsInfoOpen(false)}
                >
                  <motion.div
                    className="chatbot-info-modal"
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="modal-header">
                      <h4>{lang === 'it' ? 'Tecnologia AI' : 'AI Technology'}</h4>
                      <button onClick={() => setIsInfoOpen(false)}><FaTimes /></button>
                    </div>
                    <div className="modal-body">
                      <p>
                        {lang === 'it'
                          ? 'Questo assistente virtuale rappresenta l\'integrazione di tecnologie AI nel web design moderno.'
                          : 'This virtual assistant represents the integration of cutting-edge AI technologies into modern web design.'}
                      </p>
                      <ul>
                        <li>
                          <strong>Modello:</strong> Qwen 2.5 (by Alibaba Cloud)
                        </li>
                        <li>
                          <strong>Hosting:</strong> Hugging Face (Inference Endpoints)
                        </li>
                        <li>
                          <strong>Training:</strong> {lang === 'it'
                            ? 'Istruito specificamente sui progetti e le competenze di Davide Balice.'
                            : 'Specifically trained on Davide Balice\'s projects and skills.'}
                        </li>
                        <li>
                          <strong>Architettura:</strong> RAG (Retrieval-Augmented Generation)
                        </li>
                      </ul>
                      <p>
                        {lang === 'it'
                          ? 'L\'AI è in grado di rispondere a domande tecniche, fornire preventivi indicativi e spiegare nel dettaglio le soluzioni offerte.'
                          : 'The AI can answer technical questions, provide indicative quotes, and explain the solutions offered in detail.'}
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button className="modal-close-action" onClick={() => setIsInfoOpen(false)}>Ho capito</button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        className="chatbot-launcher"
        onClick={toggleChat}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <div className="chatbot-launcher-content">
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="launcher-icon-wrapper"
              >
                <FaTimes className="launcher-icon" />
              </motion.div>
            ) : (
              <motion.div
                key="chat"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="launcher-icon-wrapper"
              >
                <BsChatDotsFill className="launcher-icon" />
                <span className="launcher-text">Chatbot AI</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.button>
    </div>
  );
};

export default Chatbot;
